import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/utils/helpers";
import type { StoryboardScene } from "@/types/productionBrief.type";
import { cameraLine, formatSeconds, pictureBox, rangeLabel, type ShotTiming } from "./briefParts";
import { BriefPicture, ChooseVisualTile, FieldLabel, resolveBriefPicture } from "./BriefPrimitives";

/** Compact picture widths: a shot keeps 132 (Brief shot row · Compact), a slide 104 (Slide row · Stacked). */
const COMPACT_SHOT = 132;
const COMPACT_SLIDE = 104;

/**
 * The row frame both shot and slide rows share. When the brief is 52rem wide
 * or more (the post page from a 1024 viewport up): number (76), picture, what
 * we see (fill), lanes (380), 28 apart, 28 above and below.
 *
 * Below 52rem a shot is Brief shot row · Compact: the picture at 132 beside one
 * column (the number line, what we see, then the lanes). A slide (`stacked`)
 * is Slide row · Stacked: the number line, the picture at 104 beside what we
 * see, then the lanes at full width. Rows end on a hairline that takes no space.
 */
export function BriefRowFrame({
  id,
  aspect,
  number,
  picture,
  body,
  lanes,
  stacked = false,
}: {
  id?: string;
  aspect: string | null | undefined;
  number: ReactNode;
  picture: ReactNode;
  body: ReactNode;
  lanes: ReactNode;
  stacked?: boolean;
}) {
  const wide = pictureBox(aspect);
  const compactW = stacked ? COMPACT_SLIDE : Math.min(COMPACT_SHOT, wide.width);
  const vars = {
    "--brief-pic-w": `${wide.width}px`,
    "--brief-pic-w-compact": `${compactW}px`,
  } as CSSProperties;
  return (
    <article
      id={id}
      style={vars}
      className={cn(
        "grid scroll-mt-24 grid-cols-[var(--brief-pic-w-compact)_minmax(0,1fr)] py-6 shadow-[inset_0_-1px_0_var(--co-line)]",
        stacked ? "gap-x-4 gap-y-3.5" : "gap-x-6 gap-y-2.5",
        "@[52rem]/briefdoc:grid-cols-[76px_var(--brief-pic-w)_minmax(0,1fr)_380px] @[52rem]/briefdoc:gap-x-7 @[52rem]/briefdoc:gap-y-0 @[52rem]/briefdoc:py-7",
      )}
    >
      <div
        className={cn(
          "row-start-1 flex flex-wrap gap-y-1 @[52rem]/briefdoc:col-span-1 @[52rem]/briefdoc:col-start-1 @[52rem]/briefdoc:flex-col @[52rem]/briefdoc:items-start @[52rem]/briefdoc:gap-1",
          stacked ? "col-span-2 col-start-1 items-end gap-x-2" : "col-start-2 items-center gap-x-2.5",
        )}
      >
        {number}
      </div>
      <div
        className={cn(
          "col-start-1 @[52rem]/briefdoc:col-start-2 @[52rem]/briefdoc:row-span-1 @[52rem]/briefdoc:row-start-1",
          stacked ? "row-start-2" : "row-span-3 row-start-1",
        )}
      >
        {picture}
      </div>
      <div className="col-start-2 row-start-2 min-w-0 @[52rem]/briefdoc:col-start-3 @[52rem]/briefdoc:row-start-1">{body}</div>
      <div
        className={cn(
          "row-start-3 flex min-w-0 flex-col @[52rem]/briefdoc:col-span-1 @[52rem]/briefdoc:col-start-4 @[52rem]/briefdoc:row-start-1 @[52rem]/briefdoc:gap-4 @[52rem]/briefdoc:pt-0",
          stacked ? "col-span-2 col-start-1 gap-4" : "col-start-2 gap-3 pt-1.5",
        )}
      >
        {lanes}
      </div>
    </article>
  );
}

/** A lane: a 12.5 muted label and a 15 value; empty shows "None" in muted. */
export function Lane({ label, value, children }: { label: string; value?: string | null; children?: ReactNode }) {
  const text = value?.trim();
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      {text ? (
        <p dir="auto" className="mt-1 whitespace-pre-wrap text-[15px] leading-[1.45] text-co-text [unicode-bidi:plaintext]">{text}</p>
      ) : (
        <p className="mt-1 text-[15px] leading-[1.45] text-co-muted">None</p>
      )}
      {children}
    </div>
  );
}

/** Only web links are followed; anything else reads as plain text. */
function safeHref(url?: string): string | null {
  const value = url?.trim() ?? "";
  return /^(https?:\/\/|\/(?!\/))/i.test(value) ? value : null;
}

/**
 * The muted lines under what we see: a production note, retouching, and the
 * reference with its link. Each shows only when filled.
 */
export function RowNotes({
  note,
  retouching,
  referenceLabel,
  referenceUrl,
}: {
  note?: string;
  retouching?: string;
  referenceLabel?: string;
  referenceUrl?: string;
}) {
  const noteText = note?.trim();
  const retouchText = retouching?.trim();
  const label = referenceLabel?.trim();
  const url = referenceUrl?.trim();
  const href = safeHref(url);
  if (!noteText && !retouchText && !label && !url) return null;
  return (
    <div className="mt-2 flex flex-col gap-1 text-[13.5px] leading-snug text-co-muted">
      {noteText ? <p className="whitespace-pre-wrap">Note · {noteText}</p> : null}
      {retouchText ? <p className="whitespace-pre-wrap">Retouching · {retouchText}</p> : null}
      {label || url ? (
        <p className="min-w-0 truncate">
          Reference ·{" "}
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-co-line2 underline-offset-2 transition-colors hover:text-co-text"
            >
              {label || url}
            </a>
          ) : (
            label || url
          )}
        </p>
      ) : null}
    </div>
  );
}

/** Description: `visual`, then `action` as a second sentence when it adds something. */
export function shotDescription(scene: Pick<StoryboardScene, "visual" | "action">): string {
  const visual = scene.visual?.trim() ?? "";
  const action = scene.action?.trim() ?? "";
  if (!action || visual.toLowerCase().includes(action.toLowerCase())) return visual;
  if (!visual) return action;
  return `${/[.!?]$/.test(visual) ? visual : `${visual}.`} ${action}`;
}

export function BriefShotRow({
  id,
  scene,
  label,
  timing,
  aspect,
  onChooseVisual,
}: {
  id?: string;
  scene: StoryboardScene;
  /** "01", or "End" for the end frame. */
  label: string;
  timing: ShotTiming;
  aspect?: string | null;
  /** Present when the reader can save a pick: the shot's picture opens the picker (empty or filled). */
  onChooseVisual?: () => void;
}) {
  const title = scene.title?.trim() || `Shot ${label}`;
  const description = shotDescription(scene);
  const camera = cameraLine([scene.framing, scene.shotSize, scene.cameraAngle, scene.cameraMovement, scene.lens]);
  const range = rangeLabel(timing);
  const picture = resolveBriefPicture(scene.resolvedVisual, scene.referenceUrl);
  const box = pictureBox(aspect);
  return (
    <BriefRowFrame
      id={id}
      aspect={aspect}
      number={
        <>
          <span className="font-section text-[26px] font-bold leading-none text-co-text @[52rem]/briefdoc:text-[30px]">{label}</span>
          {timing.seconds !== null ? (
            <span className="text-[15px] text-co-text">{formatSeconds(timing.seconds)}</span>
          ) : scene.duration?.trim() ? (
            <span className="text-[15px] text-co-text">{scene.duration.trim()}</span>
          ) : null}
          {range ? <span className="text-[12.5px] text-co-muted">{range}</span> : null}
        </>
      }
      picture={
        <BriefPicture
          picture={picture}
          width={box.width}
          height={box.height}
          alt={title}
          fallback={onChooseVisual ? <ChooseVisualTile label={`Choose a picture for ${title}`} onChoose={onChooseVisual} /> : undefined}
          onChange={onChooseVisual}
          changeLabel={`Change the picture for ${title}`}
          fluid
        />
      }
      body={
        <>
          <h4 className="font-sans text-[18px] font-semibold leading-snug tracking-normal text-co-text">{title}</h4>
          {description ? (
            <p className="mt-2.5 whitespace-pre-wrap text-[15px] leading-[1.5] text-co-text2">{description}</p>
          ) : null}
          {camera ? <p className="mt-2.5 text-[13.5px] leading-snug text-co-muted">{camera}</p> : null}
          {scene.sound?.trim() ? (
            <p className="mt-2.5 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 text-[13.5px] leading-snug">
              <span className="text-co-muted">SFX</span>
              <span className="text-co-text2">{scene.sound.trim()}</span>
            </p>
          ) : null}
          <RowNotes note={scene.productionNote} referenceLabel={scene.referenceLabel} referenceUrl={scene.referenceUrl} />
        </>
      }
      lanes={
        <>
          <Lane label="Voiceover" value={scene.voiceover} />
          <Lane label="On screen" value={scene.textOnScreen} />
        </>
      }
    />
  );
}
